import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface ModelOption {
  value: string;
  label: string;
  sublabel?: string;
  rightLabel?: string;
  /**
   * Short caveat shown as a chip next to the option, e.g. "needs seed image".
   * Purely informational: a badged option stays selectable, because hiding a
   * model the user asked for is more confusing than explaining why it needs
   * more wiring.
   */
  badge?: string;
}

interface ModelPickerSheetProps {
  options: ModelOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  loading?: boolean;
  chipClassName?: string;
  ariaLabel?: string;
  /** Id of the text that describes the picker, e.g. a warning about the selection. */
  ariaDescribedBy?: string;
}

const TRUNCATE_LENGTH = 24;

function truncateLabel(label: string): string {
  if (label.length <= TRUNCATE_LENGTH) return label;
  return label.slice(0, TRUNCATE_LENGTH) + '...';
}

const ModelPickerSheet: React.FC<ModelPickerSheetProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchable = false,
  loading = false,
  chipClassName = '',
  ariaLabel,
  ariaDescribedBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const chipRef = useRef<HTMLButtonElement>(null);
  const [, forceRender] = useState(0);

  useLayoutEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = searchable
    ? options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          opt.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    []
  );

  const openSheet = () => {
    forceRender((n) => n + 1);
    setIsOpen(true);
  };

  return (
    <div className="relative min-w-0" onKeyDown={handleKeyDown}>
      <button
        ref={chipRef}
        type="button"
        onClick={isOpen ? () => setIsOpen(false) : openSheet}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className={`
          select w-full min-w-0 flex items-center justify-between gap-1.5 text-left
          ${chipClassName}
        `}
      >
        {loading ? (
          <span className="text-muted text-xs">Loading...</span>
        ) : selectedOption ? (
          <div className="flex items-center justify-between flex-1 min-w-0 gap-1">
            <div className="min-w-0 flex-1">
              <span className="text-sm leading-snug truncate block" title={selectedOption.label}>
                {truncateLabel(selectedOption.label)}
              </span>
              {selectedOption.sublabel && (
                <span
                  className="block text-[10px] leading-tight text-muted truncate mt-0.5"
                  title={selectedOption.sublabel}
                >
                  {selectedOption.sublabel}
                </span>
              )}
              {selectedOption.badge && (
                <span className="block text-[10px] leading-tight text-amber-300 truncate mt-0.5">
                  {selectedOption.badge}
                </span>
              )}
            </div>
            {selectedOption.rightLabel && (
              <span className="text-success text-xs ml-1 shrink-0">{selectedOption.rightLabel}</span>
            )}
          </div>
        ) : (
          <span className="text-muted text-xs">{placeholder}</span>
        )}
        <span className="text-muted text-[9px] shrink-0">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="sheet-overlay"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="hidden lg:block">
            <PopoverSheet
              chipRef={chipRef}
              options={filteredOptions}
              selectedValue={value}
              onSelect={handleSelect}
              searchable={searchable}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>
          <div className="lg:hidden">
            <BottomSheet
              options={filteredOptions}
              selectedValue={value}
              onSelect={handleSelect}
              searchable={searchable}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onClose={() => setIsOpen(false)}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

interface SheetInnerProps {
  options: ModelOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  searchable: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

const SEARCH_BAR = ({
  searchTerm,
  onSearchChange,
}: {
  searchTerm: string;
  onSearchChange: (term: string) => void;
}) => (
  <div className="p-2 border-b border-white/10">
    <input
      type="text"
      value={searchTerm}
      onChange={(e) => onSearchChange(e.target.value)}
      placeholder="Search..."
      className="input input-sm"
      autoFocus
    />
  </div>
);

const OPTIONS_LIST: React.FC<{
  options: ModelOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
}> = ({ options, selectedValue, onSelect }) => (
  <div className="overflow-y-auto overflow-x-hidden flex-1 max-h-72">
    {options.length === 0 ? (
      <div className="px-3 py-6 text-muted text-center text-sm">No options found</div>
    ) : (
      options.map((option) => (
        <div
          key={option.value}
          onClick={() => onSelect(option.value)}
          className={`px-3 py-2.5 cursor-pointer transition-colors ${
            selectedValue === option.value
              ? 'bg-white/10'
              : 'hover:bg-white/5'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-white text-sm leading-snug break-words">{option.label}</p>
                {option.badge && (
                  <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 whitespace-nowrap">
                    {option.badge}
                  </span>
                )}
              </div>
              {option.sublabel && (
                <p className="text-muted text-xs leading-tight break-all mt-1">
                  {option.sublabel}
                </p>
              )}
            </div>
            {option.rightLabel && (
              <span className="text-success text-xs ml-2 shrink-0">
                {option.rightLabel}
              </span>
            )}
          </div>
        </div>
      ))
    )}
  </div>
);

const PopoverSheet: React.FC<
  SheetInnerProps & { chipRef: React.RefObject<HTMLButtonElement | null> }
> = ({ chipRef, options, selectedValue, onSelect, searchable, searchTerm, onSearchChange }) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect();
      let left = rect.left;
      let top = rect.bottom + 4;

      if (left + 340 > window.innerWidth) {
        left = rect.right - 340;
        if (left < 16) left = 16;
      }

      if (popoverRef.current) {
        const popoverHeight = popoverRef.current.getBoundingClientRect().height;
        if (rect.bottom + 4 + popoverHeight > window.innerHeight) {
          top = rect.top - popoverHeight - 4;
        }
      }

      setPosition({ top, left });
    }
  }, [chipRef, options, searchTerm]);

  return (
    <div
      ref={popoverRef}
      className="sheet-content popover flex flex-col"
      style={{ top: position.top, left: position.left }}
    >
      {searchable && (
        <SEARCH_BAR searchTerm={searchTerm} onSearchChange={onSearchChange} />
      )}
      <OPTIONS_LIST options={options} selectedValue={selectedValue} onSelect={onSelect} />
    </div>
  );
};

const BottomSheet: React.FC<
  SheetInnerProps & { onClose: () => void }
> = ({ options, selectedValue, onSelect, searchable, searchTerm, onSearchChange, onClose }) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useLayoutEffect(() => {
    if (sheetRef.current) {
      sheetRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <div ref={sheetRef} className="sheet-content bottom flex flex-col">
      <div className="flex items-center justify-center pt-3 pb-1">
        <div className="w-10 h-1 rounded-full bg-white/20" />
      </div>
      {searchable && (
        <SEARCH_BAR searchTerm={searchTerm} onSearchChange={onSearchChange} />
      )}
      <OPTIONS_LIST options={options} selectedValue={selectedValue} onSelect={onSelect} />
      <div className="p-2 border-t border-white/10">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-sm btn-ghost w-full text-center"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ModelPickerSheet;

export type { ModelOption };